import { AxiosService } from './axios.service'
import { SearchService } from './search.service'
import {
	paginationSuccessHandler,
	paginationErrorHandler,
} from './pagination-handlers'

/**
 * Defines the API methods for a client
 */
export class ClientService extends SearchService {
	static baseURL = 'cliente'

	/**
	 * Gets the balance points of current client
	 * @return {Promise<long>} The balance points
	 */
	static getBalancePoints() {
		return AxiosService.get(`${ClientService.baseURL}/saldo-de-pontos`).then(
			({ data }) => data
		)
	}

	/**
	 * Gets the points to expire of current client
	 * @return {Promise<long>} The points to expire
	 */
	static getClientPointsExpiringIn(days) {
		return AxiosService.get(
			`${ClientService.baseURL}/pontos-a-expirar/${days}`
		).then(({ data }) => data)
	}

	/**
	 * Searches the clients
	 * @return {Promise<any[]>} List of clients
	 */
	static search(params) {
		return AxiosService.get(`${ClientService.baseURL}/consultar`, {
			params,
		})
			.then(paginationSuccessHandler)
			.catch(paginationErrorHandler)
	}

	/**
	 * Gets the extract of points of current client
	 * @return {Promise<any[]>} Client's extract
	 */
	static getExtractOfPoints(params) {
		return AxiosService.get(`${ClientService.baseURL}/extrato-de-pontos`, {
			params,
		})
			.then(paginationSuccessHandler)
			.catch(paginationErrorHandler)
	}

	/**
	 * Activates a client
	 * @param {object} params The clientId to be activate
	 */
	static activate(params) {
		return AxiosService.post(`${ClientService.baseURL}/ativar`, params).then(
			({ data }) => data
		)
	}

	/**
	 * Inactivates a client
	 * @param {object} params The clientId to be inactivate
	 */
	static inactivate(params) {
		return AxiosService.post(
			`${ClientService.baseURL}/inativar`,
			params
		).then(({ data }) => data)
	}

	/**
	 * Query the total value for an amount of points
	 * @param {object} points The amount of points to convert
	 * @param {boolean} isGift Is gift purshase
	 */
	static convertPointsToReais(points, isGift) {
		return AxiosService.get(
			`${ClientService.baseURL}/converte-pontos-em-reais/${points}/${isGift}`
		).then(({ data }) => data)
	}

	/**
	 * Get the data from logged user
	 */
	static getData() {
		return AxiosService.get(`${ClientService.baseURL}/dados`).then(
			({ data }) => data
		)
	}

	/**
	 * Query clients passing params
	 * @param {Name, Cpf, MoveflexNumber, State, City, Active, Sidx, Sord, Page, RowsPerPage} params
	 */
	static query(params) {
		return AxiosService.get(`${ClientService.baseURL}/consultar`, {
			params,
		}).then(({ data }) => data)
	}

	/**
	 * Buy points
	 * @param {object}
	 * shape {
	 *  "creditCard": {
	 *     "numberOfInstallments": 0,
	 *     "cardholderName": "string",
	 *     "cardNumber": "string",
	 *     "expirationMonth": "string",
	 *     "expirationYear": "string",
	 *     "securityCode": "string",
	 *     "transactionType": "string"
	 *   },
	 *   "isGift": true,
	 *   "destinationCpf": "string",
	 *   "points": 0,
	 *   "value": 0,
	 *   "billingAddressId": 0,
	 *   "termsAndConditionsRead": true
	 *  }
	 */
	static buyPointsByCreditCard(params) {
		return AxiosService.post(
			`${ClientService.baseURL}/comprar-pontos-via-cartao-credito`,
			params
		).then(({ data }) => data)
	}

	/**
	 * Buy points
	 * @param {object}
	 * shape {
	 *  "debtCard": {
	 *     "numberOfInstallments": 0,
	 *     "cardholderName": "string",
	 *     "cardNumber": "string",
	 *     "expirationMonth": "string",
	 *     "expirationYear": "string",
	 *     "securityCode": "string",
	 *     "transactionType": "string"
	 *   },
	 *   "isGift": true,
	 *   "destinationCpf": "string",
	 *   "points": 0,
	 *   "value": 0,
	 *   "billingAddressId": 0,
	 *   "termsAndConditionsRead": true
	 *  }
	 */
	static buyPointsByDebitCard(params) {
		return AxiosService.post(
			`${ClientService.baseURL}/comprar-pontos-via-cartao-debito`,
			params
		).then(({ data }) => data)
	}

	/**
	 * Buy points
	 * @param { object }
	 * shape {
	 * "isGift": true,
	 * "destinationCpf": "string",
	 * "points": 0,
	 * "value": 0,
	 * "billingAddressId": 0,
	 * "termsAndConditionsRead": true
	 *  }
	 */
	static buyPointsByBilling(params) {
		return AxiosService.post(
			`${ClientService.baseURL}/comprar-pontos-via-boleto`,
			params
		).then(({ data }) => data)
	}

	/**
	 * Buy points
	 * @param { object }
	 * shape {
	 * "isGift": true,
	 * "destinationCpf": "string",
	 * "points": 0,
	 * "value": 0,
	 * "billingAddressId": 0,
	 * "termsAndConditionsRead": true,
	 * "providerBank": "string"
	 *  }
	 */
	static buyPointsByEletronicTransfer(params) {
		return AxiosService.post(
			`${ClientService.baseURL}/comprar-pontos-via-transferencia-eletronica`,
			params
		).then(({ data }) => data)
	}

	/**
	 *
	 * @memberof ClientService
	 * @param {string} cpf
	 * It queries a user name by CPF number
	 */
	static getUsernameByCPF(CPF) {
		return AxiosService.get(
			`${ClientService.baseURL}/buscar-nome-por-cpf/${CPF}`
		).then(({ data }) => data)
	}

	/**
	 * Gets remaining points user can buy
	 * @return {Promise<long>} The amount of points available
	 */
	static getAvailablePointsToBuy() {
		return AxiosService.get(
			`${ClientService.baseURL}/saldo-de-pontos-disponivel-para-compras`
		).then(({ data }) => data)
	}

	/**
	 * Gets the user subscription
	 * @return {Promise<any[]>} The subscription details
	 */
	static getSubscription() {
		return AxiosService.get(`${ClientService.baseURL}/assinatura`).then(
			({ data }) => data
		)
	}

	/**
	 * Gets the client's available plans
	 * @return {Promise<any[]>} An array of plans
	 */
	static getPlans() {
		return AxiosService.get(`${ClientService.baseURL}/planos`).then(
			({ data }) => data
		)
	}

	/**
	 * Check existing CPF document to user
	 * @param {object} params The value of document
	 */
	static checkExistingPDFDocument(params) {
		return AxiosService.post(
			`${ClientService.baseURL}/verificar-existencia-cpf`,
			params
		).then(({ data }) => data)
	}

	/**
	 * Check existing RG document to user
	 * @param {object} params The value of document
	 */
	static checkExistingRGDocument(params) {
		return AxiosService.post(
			`${ClientService.baseURL}/verificar-existencia-rg`,
			params
		).then(({ data }) => data)
	}
}
